import { FC } from 'react'

import { RenderIf } from 'components/RenderIf'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useUpdateBucketProduct } from 'featureSliced/shared/hooks/openModals/useOpenProductModal/useUpdateBucketProduct'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'
import { findCatalogProduct } from 'featureSliced/shared/utils/findCatalogProduct'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'
import {
  cartLineTotal,
  menuExtrasPerUnit,
} from 'featureSliced/shared/utils/cartLineTotal'
import { Time } from 'featureSliced/widgets/TimeLocationNew/Time'
import { Location } from 'featureSliced/widgets/TimeLocationNew/Location'

import { WebLineComponents } from './WebLineComponents'

import cameraIcon from 'assets/cameraIcon.svg'
import editIcon from 'assets/basket_small.svg'
import trashCan from 'assets/trashCan.svg'

import {
  WebCardLabel,
  WebCardValue,
  WebCartLine,
  WebCartLineBody,
  WebCartThumb,
  WebCartTitle,
  WebDivider,
  WebIconButton,
  WebLineActions,
  WebRemoveButton,
  WebSummaryPanel,
  WebTextButton,
  WebTotalRow,
  WebTotalValue,
} from './styled'

const IS_CLICK_COLLECT = process.env.REACT_APP_IS_CLIC === '1'

type WebOrderSummaryProps = {
  /** back to the menu, where the cart column lives */
  onEdit?: () => void
}

/**
 * Order recap beside the checkout form. Every line carries permanent edit and
 * remove controls (QRM-649) — removing the last line trips the page's existing
 * empty-cart guard, which navigates back to the menu.
 */
export const WebOrderSummary: FC<WebOrderSummaryProps> = ({ onEdit }) => {
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const openBucketProduct = useUpdateBucketProduct()
  const { getProductName } = useGetTranslatedNames()
  const { getProductPrice } = useGetProductPrice()
  const { removeProductFromBucket } = QRMBucketSlice.actions

  const lines = useMySelector((state) => state.bucket.products)
  const productList = useMySelector((state) =>
    state.categories.items.flatMap((cat) => cat.products)
  )
  const { Currency } = useMySelector((state) => state.app.companyData)

  const priceOf = (productId: number, fallback: number) => {
    const product = findCatalogProduct(productList, productId)
    if (!product) return fallback
    return getProductPrice(product, product.categoryId || 0).finalPrice
  }

  const total = lines.reduce(
    (acc, line) =>
      acc + cartLineTotal(line, priceOf(line.productId, line.price)),
    0
  )

  return (
    <WebSummaryPanel data-testid="web-order-summary">
      <WebCartTitle>
        <WebCardValue as="p">{t('order.yourOrder')}</WebCardValue>
        <RenderIf condition={!!onEdit}>
          <WebTextButton type="button" onClick={onEdit}>
            {t('buttons.change')}
          </WebTextButton>
        </RenderIf>
      </WebCartTitle>

      <RenderIf condition={IS_CLICK_COLLECT}>
        <Time />
        <Location />
        <WebDivider />
      </RenderIf>

      {lines.map((line) => {
        const product = findCatalogProduct(productList, line.productId)
        const unit =
          priceOf(line.productId, line.price) + menuExtrasPerUnit(line)
        return (
          <WebCartLine key={line.uniqueId}>
            <WebCartThumb src={product?.imageUrl || cameraIcon} alt="" />
            <WebCartLineBody>
              <WebCardValue>
                {product ? getProductName(product) : ''}
              </WebCardValue>
              <WebLineComponents line={line} />
              <WebCardLabel>
                {`${convertToPrice(line.count)} × ${convertToPrice(unit)}`}
              </WebCardLabel>
              <WebLineActions>
                <RenderIf condition={!!product}>
                  <WebIconButton
                    type="button"
                    aria-label={t('buttons.edit')}
                    title={t('buttons.edit')}
                    onClick={() => openBucketProduct(line)}
                  >
                    <img src={editIcon} alt="" />
                  </WebIconButton>
                </RenderIf>
                <WebRemoveButton
                  type="button"
                  aria-label={t('buttons.remove')}
                  title={t('buttons.remove')}
                  onClick={() =>
                    dispatch(removeProductFromBucket(line.uniqueId))
                  }
                >
                  <img src={trashCan} alt="" />
                </WebRemoveButton>
              </WebLineActions>
            </WebCartLineBody>
            <WebCardValue as="span">
              {convertToPrice(unit * line.count)}
            </WebCardValue>
          </WebCartLine>
        )
      })}

      <WebDivider />

      <WebTotalRow>
        <span>{t('common.total')}</span>
        <WebTotalValue>
          {`${convertToPrice(total)} ${Currency || ''}`}
        </WebTotalValue>
      </WebTotalRow>
    </WebSummaryPanel>
  )
}
